import type {
  ConversationResponse,
  ConversationWithMessagesResponse,
  CreateConversationRequest,
  CreateProjectRequest,
  CreditBalance,
  CritiqueFinding,
  CritiqueSummary,
  DrcResponse,
  FileDownloadResponse,
  FileHistoryResponse,
  FileUploadResponse,
  JobResponse,
  ListConversationsResponse,
  ListFilesResponse,
  ListJobsResponse,
  ListModelsResponse,
  ListProjectsResponse,
  ProjectResponse,
  SubmitDrcRequest,
  SubmitGerberJobRequest,
  SubmitSimulationJobRequest,
  SubmitSkidlJobRequest,
  UpdateProjectRequest,
  UsageResponse,
  UserProfile,
} from "@ratsnest/shared";

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  "http://localhost:8787";

class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text();
    let message = errorBody;
    try {
      const parsed = JSON.parse(errorBody) as {
        error?: string;
        message?: string;
      };
      message = parsed.error || parsed.message || errorBody;
    } catch {
      // Use raw text
    }
    throw new ApiError(response.status, response.statusText, message);
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) return {} as T;

  return JSON.parse(text) as T;
}

// Projects API
export const projects = {
  list: () => request<ListProjectsResponse>("/api/projects"),

  get: (id: string) => request<ProjectResponse>(`/api/projects/${id}`),

  create: (data: CreateProjectRequest) =>
    request<ProjectResponse>("/api/projects", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: UpdateProjectRequest) =>
    request<ProjectResponse>(`/api/projects/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<void>(`/api/projects/${id}`, {
      method: "DELETE",
    }),
};

// Files API
export const files = {
  list: (projectId: string) =>
    request<ListFilesResponse>(`/api/projects/${projectId}/files`),

  get: async (versionId: string): Promise<FileDownloadResponse> => {
    const url = `${API_BASE}/api/files/${versionId}`;
    const response = await fetch(url, { credentials: "include" });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new ApiError(response.status, response.statusText, errorBody);
    }

    const content = await response.text();
    const contentType = response.headers.get("Content-Type") || undefined;
    return { content, contentType };
  },

  getHistory: (projectId: string, filename: string) =>
    request<FileHistoryResponse>(
      `/api/projects/${projectId}/files/history?filename=${encodeURIComponent(filename)}`
    ),

  upload: async (
    projectId: string,
    filename: string,
    content: string,
    fileType: string = "source"
  ): Promise<FileUploadResponse> => {
    const url = `${API_BASE}/api/projects/${projectId}/files`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "text/plain",
        "X-Filename": filename,
        "X-File-Type": fileType,
      },
      body: content,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      let message = errorBody;
      try {
        const parsed = JSON.parse(errorBody) as {
          error?: string;
          message?: string;
        };
        message = parsed.error || parsed.message || errorBody;
      } catch {
        // Use raw text
      }
      throw new ApiError(response.status, response.statusText, message);
    }

    return response.json() as Promise<FileUploadResponse>;
  },

  uploadBinary: async (
    projectId: string,
    filename: string,
    content: ArrayBuffer,
    fileType: string,
    contentType: string = "application/octet-stream"
  ): Promise<FileUploadResponse> => {
    const url = `${API_BASE}/api/projects/${projectId}/files`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": contentType,
        "X-Filename": filename,
        "X-File-Type": fileType,
      },
      body: content,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      let message = errorBody;
      try {
        const parsed = JSON.parse(errorBody) as {
          error?: string;
          message?: string;
        };
        message = parsed.error || parsed.message || errorBody;
      } catch {
        // Use raw text
      }
      throw new ApiError(response.status, response.statusText, message);
    }

    return response.json() as Promise<FileUploadResponse>;
  },

  delete: (projectId: string, filename: string) =>
    request<{ success: boolean }>(
      `/api/projects/${projectId}/files/${encodeURIComponent(filename)}`,
      {
        method: "DELETE",
      }
    ),

  rename: (projectId: string, oldFilename: string, newFilename: string) =>
    request<FileUploadResponse>(`/api/projects/${projectId}/files/rename`, {
      method: "POST",
      body: JSON.stringify({ oldFilename, newFilename }),
    }),

  deleteFolder: (projectId: string, folderPath: string) =>
    request<{ success: boolean; deletedCount: number }>(
      `/api/projects/${projectId}/files/folder/${encodeURIComponent(folderPath)}`,
      {
        method: "DELETE",
      }
    ),

  renameFolder: (
    projectId: string,
    oldFolderPath: string,
    newFolderPath: string
  ) =>
    request<{ success: boolean; renamedCount: number }>(
      `/api/projects/${projectId}/files/rename-folder`,
      {
        method: "POST",
        body: JSON.stringify({ oldFolderPath, newFolderPath }),
      }
    ),
};

// Jobs API
export const jobs = {
  list: (projectId: string) =>
    request<ListJobsResponse>(`/api/projects/${projectId}/jobs`),

  get: (jobId: string) => request<JobResponse>(`/api/jobs/${jobId}`),

  submitSimulation: (
    projectId: string,
    data: Omit<SubmitSimulationJobRequest, "projectId">
  ) =>
    request<JobResponse>(`/api/projects/${projectId}/jobs/simulate`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  submitSkidl: (
    projectId: string,
    data: Omit<SubmitSkidlJobRequest, "projectId">
  ) =>
    request<JobResponse>(`/api/projects/${projectId}/jobs/skidl`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  validateVerilog: (
    projectId: string,
    data: { verilogCode: string; topModule?: string; runLint?: boolean }
  ) =>
    request<{ result: Record<string, unknown> }>(
      `/api/projects/${projectId}/jobs/verilog/validate`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    ),

  simulateVerilog: (
    projectId: string,
    data: {
      verilogCode: string;
      topModule?: string;
      stopTime?: string;
      stepTime?: string;
    }
  ) =>
    request<{ result: Record<string, unknown> }>(
      `/api/projects/${projectId}/jobs/verilog/simulate`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    ),

  submitGerber: (
    projectId: string,
    data: Omit<SubmitGerberJobRequest, "projectId">
  ) =>
    request<JobResponse>(`/api/projects/${projectId}/jobs/gerbers`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  runDrc: (projectId: string, data: SubmitDrcRequest) =>
    request<DrcResponse>(`/api/projects/${projectId}/jobs/drc`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  submitPcb: (projectId: string, data: { netlistFilename: string }) =>
    request<{
      success: boolean;
      pcbFilename: string;
      componentCount?: number;
      netCount?: number;
      routingStats?: Record<string, unknown>;
    }>(`/api/projects/${projectId}/jobs/pcb`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  runCritique: (projectId: string, netlistFilename?: string) =>
    request<{
      netlistFilename: string;
      findings: CritiqueFinding[];
      summary: CritiqueSummary;
    }>(`/api/projects/${projectId}/jobs/critique`, {
      method: "POST",
      body: JSON.stringify({ netlist_filename: netlistFilename }),
    }),

  fetchPcbModels: (projectId: string, pcbFilename?: string) =>
    request<{
      placements: Array<{
        ref: string;
        model_key: string;
        x_mm: number;
        y_mm: number;
        rotation_deg: number;
        side: string;
        model_offset: { x: number; y: number; z: number };
        model_scale: { x: number; y: number; z: number };
        model_rotate: { x: number; y: number; z: number };
      }>;
      unique_models: string[];
      board_width_mm: number;
      board_height_mm: number;
      board_origin_x_mm?: number;
      board_origin_y_mm?: number;
    }>(`/api/projects/${projectId}/jobs/pcb-models`, {
      method: "POST",
      body: JSON.stringify({ pcbFilename }),
    }),

  fetchWrlModel: (projectId: string, modelKey: string) =>
    request<{ wrl: string }>(`/api/projects/${projectId}/jobs/pcb-models/wrl`, {
      method: "POST",
      body: JSON.stringify({ modelKey }),
    }),

  download: async (jobId: string): Promise<ArrayBuffer> => {
    const url = `${API_BASE}/api/jobs/${jobId}/download`;
    const response = await fetch(url, { credentials: "include" });
    if (!response.ok) {
      throw new ApiError(
        response.status,
        response.statusText,
        "Failed to download job result"
      );
    }
    return response.arrayBuffer();
  },
};

// Chat API (for non-streaming requests)
export const chat = {
  send: async (projectId: string, message: string, context?: string) => {
    return request<{ messageId: string }>("/api/chat", {
      method: "POST",
      body: JSON.stringify({ projectId, message, context }),
    });
  },
};

// Models API
export const models = {
  list: () => request<ListModelsResponse>("/api/models"),
};

// User/Credits API
export const credits = {
  get: () => request<CreditBalance>("/api/user/credits"),
};

// User profile & usage API
export const user = {
  profile: () => request<UserProfile>("/api/user/profile"),

  updateProfile: (data: { name: string }) =>
    request<{ name: string }>("/api/user/profile", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  usage: (params?: { page?: number; limit?: number; operation?: string }) => {
    const search = new URLSearchParams();
    if (params?.page) search.set("page", String(params.page));
    if (params?.limit) search.set("limit", String(params.limit));
    if (params?.operation) search.set("operation", params.operation);
    const qs = search.toString();
    return request<UsageResponse>(`/api/user/usage${qs ? `?${qs}` : ""}`);
  },

  deleteAccount: () =>
    request<{ success: boolean }>("/api/user/account", { method: "DELETE" }),

  revokeAllSessions: () =>
    request<{ success: boolean }>("/api/user/sessions/revoke-all", {
      method: "POST",
    }),
};

// Conversations API
export const conversations = {
  list: (projectId: string) =>
    request<ListConversationsResponse>(
      `/api/projects/${projectId}/conversations`
    ),

  get: (projectId: string, conversationId: string) =>
    request<ConversationWithMessagesResponse>(
      `/api/projects/${projectId}/conversations/${conversationId}`
    ),

  create: (projectId: string, data?: CreateConversationRequest) =>
    request<ConversationResponse>(`/api/projects/${projectId}/conversations`, {
      method: "POST",
      body: JSON.stringify(data ?? {}),
    }),

  delete: (projectId: string, conversationId: string) =>
    request<{ success: boolean }>(
      `/api/projects/${projectId}/conversations/${conversationId}`,
      {
        method: "DELETE",
      }
    ),
};

export const api = {
  projects,
  files,
  jobs,
  chat,
  models,
  conversations,
  credits,
  user,
};

export { ApiError };
